import { db } from "~/db/db";

let documentoSetCache = null;
let documentoSetCachePromise = null;
const documentoValidationResultCache = new Map();

function normalizeDocumento(value) {
  return String(value ?? "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
}

async function getDocumentoSetCache() {
  if (documentoSetCache) {
    return documentoSetCache;
  }

  if (!documentoSetCachePromise) {
    documentoSetCachePromise = db.documentos.toArray().then((allDocs) => {
      const normalizedSet = new Set(
        allDocs
          .map((doc) => normalizeDocumento(doc?.documento))
          .filter(Boolean)
      );

      documentoSetCache = {
        isEmpty: allDocs.length === 0,
        normalizedSet,
      };

      return documentoSetCache;
    }).finally(() => {
      documentoSetCachePromise = null;
    });
  }

  return documentoSetCachePromise;
}

// ========== Funções Auxiliares de Cálculo ==========

/**
 * Calcula o dígito verificador do CPF
 * @param {number[]} digits - Array de dígitos do CPF
 * @param {number} index1 - Índice do primeiro dígito (9)
 * @param {number} index2 - Índice do segundo dígito (10)
 * @param {number} factor1 - Fator para primeiro dígito (10)
 * @param {number} factor2 - Fator para segundo dígito (11)
 * @returns {boolean}
 */
function validateCPFDigits(digits, index1, index2, factor1, factor2) {
  const calcDigit = (slice, factor) => {
    let sum = slice.reduce((acc, num, idx) => acc + (num * (factor - idx)), 0);
    let remainder = 11 - (sum % 11);
    return remainder >= 10 ? 0 : remainder;
  };

  const firstDigit = calcDigit(digits.slice(0, 9), factor1);
  const secondDigit = calcDigit(digits.slice(0, 10), factor2);

  return firstDigit === digits[index1] && secondDigit === digits[index2];
}

/**
 * Calcula o dígito verificador do CNPJ
 * @param {number[]} digits - Array de dígitos do CNPJ
 * @returns {boolean}
 */
function validateCNPJDigits(digits) {
  const calcDigit = (slice, initialFactor) => {
    let factor = initialFactor;
    let sum = slice.reduce((acc, num) => {
      const result = num * factor;
      factor = factor === 2 ? 9 : factor - 1;
      return acc + result;
    }, 0);

    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const firstDigit = calcDigit(digits.slice(0, 12), 5);
  const secondDigit = calcDigit(digits.slice(0, 13), 6);

  return firstDigit === digits[12] && secondDigit === digits[13];
}

export function validateCNPJWithAlpha(cnpj) {
  if (!cnpj) return false;

  const clean = cnpj.replace(/[^\da-zA-Z]/g, '').toUpperCase();

  if (clean.length !== 14) return false;

  const base = clean.slice(0, 12);
  const dvInformado = clean.slice(12);

  // Converte caractere para valor numérico (ASCII - 48)
  function charToValue(char) {
    return char.charCodeAt(0) - 48;
  }

  function calcularDV(base, pesos) {
    let soma = 0;

    for (let i = 0; i < base.length; i++) {
      soma += charToValue(base[i]) * pesos[i];
    }

    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  }

  // Pesos padrão CNPJ
  const pesosPrimeiro = [5,4,3,2,9,8,7,6,5,4,3,2];
  const pesosSegundo  = [6,5,4,3,2,9,8,7,6,5,4,3,2];

  const primeiroDV = calcularDV(base, pesosPrimeiro);
  const segundoDV = calcularDV(base + primeiroDV, pesosSegundo);

  return dvInformado === `${primeiroDV}${segundoDV}`;
}


// ========== Funções Auxiliares de Verificação de Padrões ==========

/**
 * Verifica se a sequência é ascendente ou descendente
 * @param {number[]} slice - Slice de números
 * @returns {boolean}
 */
function isSequenceAscending(slice) {
  return slice.every((val, idx, arr) => idx === 0 || val === arr[idx - 1] + 1);
}

/**
 * Verifica se a sequência é descendente
 * @param {number[]} slice - Slice de números
 * @returns {boolean}
 */
function isSequenceDescending(slice) {
  return slice.every((val, idx, arr) => idx === 0 || val === arr[idx - 1] - 1);
}

// ========== Funções Públicas de Validação ==========

/**
 * Verifica se o número possui padrão sequencial (crescente ou decrescente)
 * @param {string} numbers - String de números
 * @param {number} length - Tamanho esperado (11 para CPF, 14 para CNPJ)
 * @returns {boolean}
 */
// export function hasSequentialPattern(numbers, length) {
//   const minSeq = length === 11 ? 5 : 8;
//   const arr = numbers.split('');

//   for (let i = 0; i <= arr.length - minSeq; i++) {
//     const slice = arr.slice(i, i + minSeq).map(Number);

//     if (isSequenceAscending(slice) || isSequenceDescending(slice)) {
//       return true;
//     }
//   }
//   return false;
// }

/**
 * Valida CPF verificando os dígitos verificadores
 * @param {string} cpf - CPF sem formatação (11 dígitos)
 * @returns {boolean}
 */
export function validateCPF(cpf) {
  if (cpf.length !== 11) return false;

  const digits = cpf.split('').map(Number);
  return validateCPFDigits(digits, 9, 10, 10, 11);
}

/**
 * Valida CNPJ verificando os dígitos verificadores
 * @param {string} cnpj - CNPJ sem formatação (14 dígitos)
 * @returns {boolean}
 */
export function validateCNPJ(cnpj) {
  if (cnpj.length !== 14) return false;

  const digits = cnpj.split('').map(Number);
  return validateCNPJDigits(digits);
}

/**
 * Valida campos básicos (tamanho do documento)
 * @param {string} value - Valor formatado
 * @param {string} numbers - Apenas números
 * @param {boolean} isPF - É pessoa física (CPF)?
 * @returns {Object|null} Erro ou null
 */
export function validateBasicFields(value, numbers, isPF) {
  const expectedLength = isPF ? 14 : 18;
  if (value.length !== expectedLength) {
    return {
      message: isPF ? "Número de CPF inválido!" : "Número de CNPJ inválido!"
    };
  }
  return null;
}

/**
 * Valida formato e padrões sequenciais/repetitivos
 * @param {string} value - Valor formatado
 * @param {string} numbers - Apenas números
 * @param {boolean} isPF - É pessoa física (CPF)?
 * @returns {Object|null} Erro ou null
 */
export function validateFormatAndSequence(value, numbers, isPF, hasAlpha = false) {
  const pattern = isPF
    ? /^\d{3}\.\d{3}\.\d{3}-\d{2}$/
    : /^[A-Za-z0-9]{2}\.[A-Za-z0-9]{3}\.[A-Za-z0-9]{3}\/[A-Za-z0-9]{4}-\d{2}$/;

  if (!pattern.test(value)) {
    return { message: isPF ? "CPF inválido" : "CNPJ inválido" };
  }

  const length = isPF ? 11 : 14;

  if (!isPF && hasAlpha) {
    return null;
  }

  // Verifica padrão repetitivo simplificado (todos os dígitos iguais)
  const isAllSame = /^(\d)\1+$/.test(numbers);
  if (isAllSame) {
    return {
      message: isPF
        ? "CPF inválido: não pode ser sequencial ou repetitivo!"
        : "CNPJ inválido: não pode ser sequencial ou repetitivo!"
    };
  }

  // Verifica padrões sequenciais
  // if (hasSequentialPattern(numbers, length)) {
  //   return {
  //     message: "CPF/CNPJ inválido: não pode ser uma sequência numérica crescente ou decrescente!"
  //   };
  // }

  return null;
}

/**
 * Valida os dígitos verificadores
 * @param {string} numbers - Apenas números
 * @param {boolean} isPF - É pessoa física (CPF)?
 * @returns {Object|null} Erro ou null
 */
export function validateDigits(numbers, isPF, hasAlpha = false) {
  if (!isPF && hasAlpha) {
    return null;
  }
  const isValid = isPF ? validateCPF(numbers) : validateCNPJ(numbers);
  if (!isValid) {
    return { message: isPF ? "CPF inválido!" : "CNPJ inválido!" };
  }
  return null;
}

/**
 * Valida se o documento já existe no banco de dados
 * @param {string} numbers - Apenas números
 * @returns {Promise<Object|null>} Erro ou null
 */
export async function validateDatabase(documentoInput) {
  const normalizedInput = normalizeDocumento(documentoInput);
  if (!normalizedInput) {
    return null;
  }

  if (documentoValidationResultCache.has(normalizedInput)) {
    return documentoValidationResultCache.get(normalizedInput);
  }

  const cache = await getDocumentoSetCache();
  if (cache.isEmpty) {
    return {
      message: "Sincronize os dados para buscar os documentos dos emissores já cadastrados!"
    };
  }

  const validationResult = cache.normalizedSet.has(normalizedInput)
    ? { message: "O emissor já consta cadastrado na base de dados!" }
    : null;

  if (documentoValidationResultCache.size > 1000) {
    documentoValidationResultCache.clear();
  }
  documentoValidationResultCache.set(normalizedInput, validationResult);

  return validationResult;
}
